import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Fuel, Flame, Droplet, TrendingUp, TrendingDown, Minus, MessageCircle } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} मिनट पहले अपडेट`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} घंटे पहले अपडेट`;
  return `${Math.floor(diff / 86400)} दिन पहले अपडेट`;
}

// Tiny inline SVG sparkline
function Sparkline({ values = [], stroke = "#DC2626", height = 28, width = 90 }) {
  const clean = values.filter((v) => v != null && !isNaN(v));
  if (clean.length < 2) return <div className="text-[9px] text-slate-400 italic">इतिहास कम</div>;
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;
  const stepX = width / (clean.length - 1);
  const pts = clean.map((v, i) => `${(i * stepX).toFixed(1)},${(height - ((v - min) / range) * (height - 4) - 2).toFixed(1)}`).join(" ");
  const first = clean[0]; const last = clean[clean.length - 1];
  const delta = last - first;
  const Icon = Math.abs(delta) < 0.05 ? Minus : (delta > 0 ? TrendingUp : TrendingDown);
  const deltaColor = Math.abs(delta) < 0.05 ? "text-slate-400" : (delta > 0 ? "text-rose-600" : "text-emerald-600");
  return (
    <div className="flex items-center gap-1.5" title={`7-day change: ${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`}>
      <svg width={width} height={height} className="overflow-visible">
        <polyline fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" points={pts}/>
        {clean.map((v, i) => {
          const cx = (i * stepX).toFixed(1);
          const cy = (height - ((v - min) / range) * (height - 4) - 2).toFixed(1);
          const isLast = i === clean.length - 1;
          return <circle key={i} cx={cx} cy={cy} r={isLast ? 2.2 : 1.4} fill={stroke} opacity={isLast ? 1 : 0.55}/>;
        })}
      </svg>
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${deltaColor}`}>
        <Icon className="w-2.5 h-2.5"/>
        {delta >= 0 ? "+" : ""}{delta.toFixed(2)}
      </span>
    </div>
  );
}

export default function FuelPricesStrip() {
  const [fp, setFp] = useState(null);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    api.get("/fuel-prices").then((r) => setFp(r.data)).catch(() => {});
    api.get("/fuel-prices/history?days=7").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  if (!fp || (!fp.petrol && !fp.diesel && !fp.cng && !fp.lpg)) return null;
  const items = [
    { key: "petrol", label: "पेट्रोल", value: fp.petrol, Icon: Fuel, color: "text-rose-600", stroke: "#DC2626", bg: "bg-rose-50", border: "border-rose-200" },
    { key: "diesel", label: "डीज़ल", value: fp.diesel, Icon: Fuel, color: "text-emerald-600", stroke: "#059669", bg: "bg-emerald-50", border: "border-emerald-200" },
    { key: "cng", label: "CNG", value: fp.cng, Icon: Flame, color: "text-sky-600", stroke: "#0284C7", bg: "bg-sky-50", border: "border-sky-200" },
    { key: "lpg", label: "LPG (14.2kg)", value: fp.lpg, Icon: Droplet, color: "text-orange-600", stroke: "#EA580C", bg: "bg-orange-50", border: "border-orange-200" },
  ].filter((x) => x.value != null);

  // Build a WhatsApp-forwardable text of today's fuel rates for trader groups
  const buildWaText = () => {
    const lines = [];
    lines.push("*⛽ आज के ईंधन के दाम — INDIAN-NEWS-21*");
    lines.push(`_${fp.city || "सूरजपुर"}_ · ${new Date().toLocaleDateString("hi-IN", { day: "numeric", month: "long", year: "numeric" })}`);
    lines.push("");
    items.forEach((it) => {
      const unit = it.key === "lpg" ? "/सिलेंडर" : "/लीटर";
      lines.push(`▪️ ${it.label}: ₹${Number(it.value).toFixed(2)}${unit}`);
    });
    if (fp.source) { lines.push(""); lines.push(`स्रोत: ${fp.source}`); }
    lines.push("");
    lines.push(`रोज़ के दाम: ${typeof window !== "undefined" ? window.location.origin : ""}`);
    lines.push("— प्रधान संपादक JAKIR MOMIN");
    return lines.join("\n");
  };
  const shareWA = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(buildWaText())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <section className="mb-8 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="fuel-prices-strip">
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white px-5 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Fuel className="w-4 h-4 text-amber-400"/>
          <span className="uppercase tracking-[0.22em] text-[10px] font-bold text-amber-300">आज के ईंधन के दाम</span>
        </div>
        <span className="text-slate-500">·</span>
        <span className="text-sm font-devnagri-heading font-bold">{fp.city}</span>
        {fp.updated_at && <span className="text-[10px] text-slate-400 ml-auto">{relTime(fp.updated_at)}</span>}
        <button onClick={shareWA} title="WhatsApp पर आज के दाम भेजें"
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold px-2.5 py-1 rounded-full transition shadow"
          data-testid="fuel-share-wa">
          <MessageCircle className="w-3 h-3"/>
          <span className="hidden sm:inline">WhatsApp</span>
        </button>
      </div>
      <div className={`grid grid-cols-2 ${items.length >= 3 ? "md:grid-cols-4" : "md:grid-cols-" + items.length} divide-x divide-slate-100`}>
        {items.map((it) => {
          const series = history.map((h) => h[it.key]);
          return (
            <div key={it.key} className={`px-4 py-4 ${it.bg} flex flex-col gap-2`} data-testid={`fuel-${it.key}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${it.bg} border ${it.border} flex items-center justify-center shrink-0`}>
                  <it.Icon className={`w-5 h-5 ${it.color}`}/>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-admin">{it.label}</div>
                  <div className={`font-devnagri-heading font-black text-xl leading-none ${it.color}`}>
                    ₹{Number(it.value).toFixed(2)}<span className="text-[10px] text-slate-400 font-normal ml-1">{it.key === "lpg" ? "/सिलेंडर" : "/लीटर"}</span>
                  </div>
                </div>
              </div>
              <div className="pl-1 flex items-center gap-2 border-t border-white/60 pt-2" data-testid={`fuel-spark-${it.key}`}>
                <span className="text-[9px] uppercase tracking-widest text-slate-500">7 दिन</span>
                <Sparkline values={series} stroke={it.stroke}/>
              </div>
            </div>
          );
        })}
      </div>
      {fp.source && <div className="px-5 py-1.5 bg-slate-50 text-[10px] text-slate-500 text-right">स्रोत: {fp.source}</div>}
    </section>
  );
}
